import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import {
  UserPlus, Wallet, ListOrdered, Mail, Lock, User2, Hash,
  Inbox, RefreshCw, Search, Users, Receipt
} from 'lucide-react';
import { supabase } from '../supabaseClient';
import { cn, formatNumber, formatDateTime } from '../lib/utils';
import { Card, CardHeader, CardTitle, CardDescription, CardBody } from './ui/Card';
import { Input, Label } from './ui/Input';
import Button from './ui/Button';
import Tabs from './ui/Tabs';
import { SkeletonRow } from './ui/Skeleton';
import PageHeader from './ui/PageHeader';
import EmptyState from './ui/EmptyState';
import StatusBadge from './ui/StatusBadge';
import { MARKETPLACE_META } from './marketplaces';
import AdminClientsTab from './AdminClientsTab';
import AdminBillingTab from './AdminBillingTab';

const TABS = [
  { id: 'create',  label: 'Utwórz konto',     icon: UserPlus },
  { id: 'clients', label: 'Klienci',          icon: Users },
  { id: 'credits', label: 'Kredyty',          icon: Wallet },
  { id: 'billing', label: 'Rozliczenia',      icon: Receipt },
  { id: 'orders',  label: 'Wszystkie zlecenia', icon: ListOrdered },
];

export default function AdminPage() {
  const [tab, setTab] = useState('create');

  // Create user form
  const [newEmail, setNewEmail] = useState('');
  const [newPass, setNewPass] = useState('');
  const [newName, setNewName] = useState('');
  const [initCredits, setInitCredits] = useState(50);

  // Add credits form
  const [creditEmail, setCreditEmail] = useState('');
  const [creditAmount, setCreditAmount] = useState(50);

  const [loading, setLoading] = useState(false);

  // Plan v9/A2: pule kredytów organizacji (agencje). Mediafy = bez limitu.
  const [orgs, setOrgs] = useState([]);
  const [orgId, setOrgId] = useState('');
  const [orgAmount, setOrgAmount] = useState(100);
  const fetchOrgs = async () => {
    const { data } = await supabase.from('organizations').select('id, name, plan, credits').order('name');
    setOrgs(data || []);
  };
  // eslint-disable-next-line
  useEffect(() => { if (tab === 'credits') fetchOrgs(); }, [tab]);
  const handleOrgCredits = async (e) => {
    e.preventDefault();
    if (!orgId) return;
    setLoading(true);
    try {
      const { data, error } = await supabase.rpc('grant_org_credits', { p_org: orgId, p_amount: parseInt(orgAmount, 10), p_reason: 'topup' });
      if (error) throw error;
      toast.success(`Pula organizacji: ${formatNumber(data)} kredytów`);
      fetchOrgs();
    } catch (err) {
      toast.error(`Błąd: ${err.message}`);
    } finally { setLoading(false); }
  };

  // Orders list
  const [allOrders, setAllOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (tab === 'orders') fetchAllOrders();
    // eslint-disable-next-line
  }, [tab]);

  const fetchAllOrders = async () => {
    setOrdersLoading(true);
    const { data } = await supabase.from('orders').select('*').order('created_at', { ascending: false });
    setAllOrders(data || []);
    setOrdersLoading(false);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await supabase.rpc('create_user_with_credits', {
        p_email: newEmail, p_password: newPass,
        p_full_name: newName, p_credits: parseInt(initCredits),
      });
      toast.success(`Konto utworzone: ${newEmail} (${initCredits} kredytów)`);
      setNewEmail(''); setNewPass(''); setNewName(''); setInitCredits(50);
    } catch (err) {
      toast.error(`Błąd: ${err.message}`);
    } finally { setLoading(false); }
  };

  const handleAddCredits = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data: profile } = await supabase.from('profiles').select('id').eq('email', creditEmail).single();
      if (!profile) throw new Error('Nie znaleziono użytkownika.');
      // Audyt 16.10: przez grant_credits (zapis w historii; członek organizacji → do PULI organizacji, Mediafy bez limitu).
      const { data: newBal, error } = await supabase.rpc('grant_credits', { p_user: profile.id, p_amount: parseInt(creditAmount, 10), p_reason: 'topup' });
      if (error && /org_unlimited/.test(error.message || '')) { toast.info('To konto należy do Mediafy — kredyty bez limitu, nic nie dodano.'); return; }
      if (error) throw error;
      const { data: m } = await supabase.from('org_members').select('organizations(name)').eq('user_id', profile.id).limit(1);
      const orgName = m?.[0]?.organizations?.name;
      toast.success(orgName ? `Konto należy do organizacji „${orgName}” — dodano ${creditAmount} kredytów do jej puli (saldo: ${formatNumber(newBal)}).` : `Dodano ${creditAmount} kredytów. Nowe saldo: ${formatNumber(newBal)}`);
      setCreditEmail(''); setCreditAmount(50);
    } catch (err) {
      toast.error(`Błąd: ${err.message}`);
    } finally { setLoading(false); }
  };

  // Search filter for orders
  const filteredOrders = allOrders.filter(o => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (o.marketplace || '').toLowerCase().includes(q) ||
      (o.language    || '').toLowerCase().includes(q) ||
      (o.email       || '').toLowerCase().includes(q) ||
      (o.filename    || '').toLowerCase().includes(q) ||
      (o.status      || '').toLowerCase().includes(q)
    );
  });

  return (
    <div className="animate-fade-in">
      <PageHeader
        title="Panel Administratora"
        subtitle="Zarządzanie kontami użytkowników, kredytami i monitoring wszystkich zleceń."
      />

      {/* Tabs */}
      <Tabs
        variant="underline"
        value={tab}
        onChange={setTab}
        tabs={TABS.map(t => ({
          id: t.id,
          label: t.label,
          count: t.id === 'orders' ? (allOrders.length || undefined) : undefined,
        }))}
        className="mb-7"
      />

      {/* CREATE USER */}
      {tab === 'create' && (
        <Card className="max-w-xl">
          <CardHeader>
            <CardTitle>Nowe konto użytkownika</CardTitle>
            <CardDescription>
              Tworzy konto + przypisuje kredyty startowe. Użytkownik dostaje hasło tymczasowe.
            </CardDescription>
          </CardHeader>
          <CardBody>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <Label htmlFor="new-email" required>Adres e-mail</Label>
                <Input
                  id="new-email"
                  type="email"
                  value={newEmail}
                  onChange={e => setNewEmail(e.target.value)}
                  placeholder="klient@firma.pl"
                  required
                  leftIcon={<Mail className="w-4 h-4" />}
                />
              </div>
              <div>
                <Label htmlFor="new-pass" required>Hasło tymczasowe</Label>
                <Input
                  id="new-pass"
                  type="password"
                  value={newPass}
                  onChange={e => setNewPass(e.target.value)}
                  placeholder="••••••••"
                  required
                  leftIcon={<Lock className="w-4 h-4" />}
                />
              </div>
              <div>
                <Label htmlFor="new-name">Imię i nazwisko (opcjonalnie)</Label>
                <Input
                  id="new-name"
                  value={newName}
                  onChange={e => setNewName(e.target.value)}
                  placeholder="Jan Kowalski"
                  leftIcon={<User2 className="w-4 h-4" />}
                />
              </div>
              <div>
                <Label htmlFor="init-credits" required>Kredyty startowe</Label>
                <Input
                  id="init-credits"
                  type="number"
                  min="0"
                  step="10"
                  value={initCredits}
                  onChange={e => setInitCredits(e.target.value)}
                  leftIcon={<Hash className="w-4 h-4" />}
                />
              </div>
              <Button
                type="submit"
                size="lg"
                loading={loading}
                leftIcon={!loading && <UserPlus className="w-4 h-4" />}
                className="w-full mt-2"
              >
                {loading ? 'Tworzenie…' : 'Utwórz konto'}
              </Button>
            </form>
          </CardBody>
        </Card>
      )}

      {/* CLIENTS DASHBOARD */}
      {tab === 'clients' && <AdminClientsTab />}
      {tab === 'billing' && <AdminBillingTab />}

      {/* ADD CREDITS */}
      {tab === 'credits' && (
        <Card className="max-w-xl">
          <CardHeader>
            <CardTitle>Doładuj kredyty użytkownika</CardTitle>
            <CardDescription>
              Dodaje kredyty do istniejącego konta na podstawie e-maila.
            </CardDescription>
          </CardHeader>
          <CardBody>
            <form onSubmit={handleAddCredits} className="space-y-4">
              <div>
                <Label htmlFor="credit-email" required>E-mail użytkownika</Label>
                <Input
                  id="credit-email"
                  type="email"
                  value={creditEmail}
                  onChange={e => setCreditEmail(e.target.value)}
                  placeholder="klient@firma.pl"
                  required
                  leftIcon={<Mail className="w-4 h-4" />}
                />
              </div>
              <div>
                <Label htmlFor="credit-amount" required>Liczba kredytów do dodania</Label>
                <Input
                  id="credit-amount"
                  type="number"
                  min="1"
                  step="10"
                  value={creditAmount}
                  onChange={e => setCreditAmount(e.target.value)}
                  leftIcon={<Wallet className="w-4 h-4" />}
                />
              </div>
              <Button
                type="submit"
                size="lg"
                variant="success"
                loading={loading}
                leftIcon={!loading && <Wallet className="w-4 h-4" />}
                className="w-full mt-2"
              >
                {loading ? 'Dodawanie…' : 'Doładuj kredyty'}
              </Button>
            </form>
          </CardBody>
        </Card>
      )}

      {tab === 'credits' && (
        <Card className="max-w-xl mt-5">
          <CardHeader>
            <CardTitle>Pula kredytów organizacji</CardTitle>
            <CardDescription>
              Członkowie organizacji (agencji) płacą z jednej puli. Ujemna liczba = korekta w dół. Mediafy = bez limitu.
            </CardDescription>
          </CardHeader>
          <CardBody>
            <div className="mb-4 divide-y divide-ink-100 dark:divide-ink-800 text-sm">
              {orgs.map(o => (
                <div key={o.id} className="flex items-center justify-between py-1.5">
                  <span className="font-medium">{o.name} <span className="text-xs text-ink-400">· {o.plan}</span></span>
                  <span className="tabular-nums">{o.plan === 'mediafy' ? 'bez limitu' : formatNumber(o.credits)}</span>
                </div>
              ))}
              {orgs.length === 0 && <div className="py-1.5 text-ink-400">Brak organizacji.</div>}
            </div>
            <form onSubmit={handleOrgCredits} className="space-y-4">
              <div>
                <Label htmlFor="org-id" required>Organizacja</Label>
                <select id="org-id" value={orgId} onChange={e => setOrgId(e.target.value)} required
                  className="w-full h-10 rounded-md border border-ink-200 dark:border-ink-700 bg-white dark:bg-ink-900 px-3 text-sm">
                  <option value="">— wybierz —</option>
                  {orgs.filter(o => o.plan !== 'mediafy').map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                </select>
              </div>
              <div>
                <Label htmlFor="org-amount" required>Kredyty (+ doładowanie / − korekta)</Label>
                <Input id="org-amount" type="number" step="10" value={orgAmount} onChange={e => setOrgAmount(e.target.value)} leftIcon={<Wallet className="w-4 h-4" />} />
              </div>
              <Button type="submit" size="lg" variant="success" loading={loading} className="w-full mt-2">Zmień pulę</Button>
            </form>
          </CardBody>
        </Card>
      )}

      {/* ALL ORDERS */}
      {tab === 'orders' && (
        <div>
          <div className="flex items-center justify-between gap-3 mb-4">
            <div className="w-full sm:w-80">
              <Input
                type="search"
                placeholder="Szukaj po e-mail, marketplace, języku, statusie…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                leftIcon={<Search className="w-4 h-4" />}
              />
            </div>
            <Button
              variant="secondary"
              size="md"
              onClick={fetchAllOrders}
              leftIcon={<RefreshCw className={cn('w-4 h-4', ordersLoading && 'animate-spin')} />}
            >
              Odśwież
            </Button>
          </div>

          {ordersLoading ? (
            <Card className="overflow-hidden">
              <div className="divide-y divide-ink-100 dark:divide-ink-800">
                {Array.from({ length: 6 }).map((_, i) => <SkeletonRow key={i} columns={6} />)}
              </div>
            </Card>
          ) : filteredOrders.length === 0 ? (
            <EmptyState
              icon={<Inbox className="w-7 h-7" />}
              title={search ? 'Brak wyników' : 'Brak zleceń'}
              description={search ? 'Spróbuj innej frazy.' : 'Żaden klient jeszcze nie uruchomił zlecenia.'}
              action={search && (
                <Button variant="secondary" size="sm" onClick={() => setSearch('')}>
                  Wyczyść wyszukiwanie
                </Button>
              )}
            />
          ) : (
            <Card className="overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-ink-50/80 dark:bg-ink-800/40 border-b border-ink-200 dark:border-ink-800">
                    <tr>
                      {['Data', 'E-mail', 'Marketplace', 'Język', 'Produkty', 'Kredyty', 'Status'].map(h => (
                        <th key={h} className="px-4 py-3 text-2xs font-bold uppercase tracking-wide text-ink-500 dark:text-ink-400 text-left whitespace-nowrap">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-ink-100 dark:divide-ink-800">
                    {filteredOrders.map(o => {
                      const meta = MARKETPLACE_META[o.marketplace];
                      return (
                        <tr key={o.id} className="hover:bg-ink-50/60 dark:hover:bg-ink-800/40 transition-colors">
                          <td className="px-4 py-3 text-xs text-ink-600 dark:text-ink-400 font-mono whitespace-nowrap">
                            {formatDateTime(o.created_at)}
                          </td>
                          <td className="px-4 py-3 text-xs text-ink-700 dark:text-ink-300 max-w-[200px] truncate">{o.email}</td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="inline-flex items-center gap-2">
                              <span
                                className="w-2 h-2 rounded-full flex-shrink-0"
                                style={{ background: meta?.color || '#A1A1AA' }}
                              />
                              <span className="text-sm font-semibold text-ink-900 dark:text-ink-100">{o.marketplace}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm text-ink-700 dark:text-ink-300 whitespace-nowrap">{o.language || '—'}</td>
                          <td className="px-4 py-3 text-sm font-semibold text-ink-900 dark:text-ink-100 whitespace-nowrap">
                            {formatNumber(o.product_count)}
                          </td>
                          <td className="px-4 py-3 text-xs font-mono text-ink-700 dark:text-ink-300 whitespace-nowrap">
                            {formatNumber(o.credits_used)}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <StatusBadge status={o.status} size="md" />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Card>
          )}

          {!ordersLoading && filteredOrders.length > 0 && (
            <div className="mt-3 text-xs text-ink-500 dark:text-ink-400 px-1">
              Wyświetlono {formatNumber(filteredOrders.length)} z {formatNumber(allOrders.length)} zleceń
            </div>
          )}
        </div>
      )}
    </div>
  );
}

